/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-organisation-members.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import StudyMembership from '@components/admin/users/study-membership/cm-ram-study-membership';
import SmStudyMembership from '@components/admin/users/study-membership/sm-study-membership/sm-study-members';
import TrialOsStudyMembership from '@components/admin/users/study-membership/trialos-study-membership';
import { buildTrialOsStudyMembershipAction } from '@components/admin/users/study-membership/trialos-study-membership/row-action';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { hasAppLicense } from '@utils-clean/app-license';
import { useAuthorization, useTranslation, useBoolean } from 'hooks';
import {
    OrganisationAndStudyMemberFields,
    OrganisationAndStudyMemberFields_Organisation_members_OrganisationMember as OrganisationAndStudyMemberFields_members,
    OrganisationAndStudyMemberFields_Organisation_roles_OrganisationRole as OrganisationAndStudyMemberFields_roles,
    OrganisationAndStudyMemberFields_Organisation_studies_Study as OrganisationAndStudyMemberFields_studies,
    StudyAndMemberFields_Study_members_StudyMember as OrganisationAndStudyMemberFields_studies_members,
} from 'queries/generated/fragments.generated';
import { GET_ORGANISATION_AND_STUDY_MEMBERS } from 'queries/uam';
import { Authentication } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import ResetPasswordModal from 'views/components/admin/reset-password-modal';
import AddUserModal from 'views/components/admin/users/add-user';
import UsersGrid, { OrganisationUserGridItem } from 'views/components/admin/users/users-grid';
import Button from 'views/components/core/buttons/button';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

const INVITED = 'INVITED';

const AdminOrganisationMembers = () => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const openAddUser = useBoolean(false);
    const openCmRamStudyMembership = useBoolean(false);
    const openSMStudyMembership = useBoolean(false);
    const openTrialOsStudyMembership = useBoolean(false);
    const openResetPassword = useBoolean(false);
    const { organisationId } = user;
    const canEdit = user?.canWriteSystemAdminLevel?.(true);
    // TrialOS admins can manage TrialOS membership without being org admins
    const canEditTrialOs = user?.canWriteSystemAdminLevel?.() || !!user?.isTrialOsAdmin?.(true);

    const [currentUser, setCurrentUser] = useState(null);

    const [getStudyMembers, { data, error, loading }] = useLazyQuery(GET_ORGANISATION_AND_STUDY_MEMBERS);

    useEffect(() => {
        getStudyMembers();
    }, [organisationId]);

    useEffect(() => {
        if (openCmRamStudyMembership.value || openSMStudyMembership.value) {
            getStudyMembers();
        }
    }, [openCmRamStudyMembership, openSMStudyMembership]);

    if (!user || (!user.isOrgAdmin() && !user.isTrialOsAdmin?.())) return null;

    if (loading) return <Loading />;

    if (error) return <Error error={error} />;

    const organisation: OrganisationAndStudyMemberFields = data?.organisation;
    const isSso = organisation?.security?.authenticationMethod === Authentication.SSO;

    const memberIsActiveInStudy = (
        member: OrganisationAndStudyMemberFields_members,
        study: OrganisationAndStudyMemberFields_studies,
    ) =>
        member.userId !== Constants.ObjectId.Empty &&
        study.members?.some(
            (studyMember: OrganisationAndStudyMemberFields_studies_members) =>
                studyMember.userId === member.userId && studyMember.active,
        );

    const memberIsActiveInSmStudy = (
        member: OrganisationAndStudyMemberFields_members,
        study: OrganisationAndStudyMemberFields_studies,
    ) =>
        member.userId !== Constants.ObjectId.Empty &&
        study.smMembers?.some((studyMember) => studyMember?.userId === member.userId && studyMember?.active);

    function getStudyData(member: OrganisationAndStudyMemberFields_members) {
        let studyCount = 0;
        let smStudyCount = 0;
        let studyList = '';

        organisation.studies.forEach((study: OrganisationAndStudyMemberFields_studies) => {
            if (memberIsActiveInStudy(member, study)) {
                studyCount += 1;
                studyList += studyList ? `, ${study.studyNumber}` : study.studyNumber;
            }

            if (memberIsActiveInSmStudy(member, study)) {
                smStudyCount += 1;
            }
        });

        return { studyCount, studyList, smStudyCount };
    }

    function getGridItem(member: OrganisationAndStudyMemberFields_members) {
        const role = organisation.roles.find(
            (orgRole: OrganisationAndStudyMemberFields_roles) => orgRole.id === member.roleId,
        );
        const { studyCount, studyList, smStudyCount } = getStudyData(member);

        return {
            ...member,
            isEnabled: member.user?.isEnabled,
            lastLoginTime: member.user?.lastLoginTime,
            role: role?.name ?? '',
            studyCount,
            studyList,
            smStudyCount,
        } as OrganisationUserGridItem;
    }

    const gridItems = organisation?.members.map((member: OrganisationAndStudyMemberFields_members) =>
        getGridItem(member),
    );


    const isEnabled = (item: any) => (item.user || item.userId !== Constants.ObjectId.Empty) && item.status !== INVITED;

    return (
        <>
            <ResetPasswordModal
                open={openResetPassword.value}
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success')
                            NotificationService.showNotification(t('login:passwordResetEmailSent'));
                        openResetPassword.setFalse();
                        setCurrentUser(null);
                    }
                }}
                email={currentUser?.user ? currentUser.user.email : currentUser?.email}
                orgCode={organisation?.loginCode}
            />
            <AddUserModal
                open={openAddUser.value}
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else if (action === 'resent') NotificationService.showNotification(t('uam:inviteResentSuccessful'));
                    else {
                        if (action === 'success') NotificationService.showNotification(t('uam:inviteSuccessfulSent'));
                        if (action === 'edit')
                            NotificationService.showNotification(
                                t('uam:userSuccessfullyUpdated', {
                                    id: currentUser?.user?.recordId ?? currentUser?.recordId,
                                }),
                            );
                        openAddUser.setFalse();
                        setCurrentUser(null);
                    }
                }}
                user={currentUser}
                loggedUser={user}
                onExistEmail={() => {
                    NotificationService.showNotification(t('uam:emailAlreadyInUse'));
                }}
                organisation={organisation}
            />
            <StudyMembership
                open={openCmRamStudyMembership.value}
                studies={organisation?.studies ?? []}
                canEdit={canEdit}
                user={currentUser}
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success')
                            NotificationService.showNotification(
                                t('uam:studyMembershipSuccessfullyUpdated', {
                                    id: currentUser?.recordId || currentUser?.id,
                                }),
                            );
                        openCmRamStudyMembership.setFalse();
                        setCurrentUser(null);
                        NavigationService.setUpdateStudies(user?.organisationKey, true);
                    }
                }}
            />
            <SmStudyMembership
                open={openSMStudyMembership.value}
                studies={organisation?.studies ?? []}
                canEdit={canEdit}
                user={currentUser}
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success')
                            NotificationService.showNotification(
                                t('uam:studyMembershipSuccessfullyUpdated', {
                                    id: currentUser?.recordId || currentUser?.id,
                                }),
                            );
                        openSMStudyMembership.setFalse();
                        setCurrentUser(null);
                        NavigationService.setUpdateStudies(user?.organisationKey, true);
                    }
                }}
            />
            <TrialOsStudyMembership
                open={openTrialOsStudyMembership.value}
                studies={organisation?.studies ?? []}
                canEdit={canEditTrialOs}
                hasRiskIqLicense={hasAppLicense(user, 'RiskIQ')}
                user={currentUser}
                onClose={(action?: 'success' | 'error') => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success')
                            NotificationService.showNotification(
                                t('uam:trialOsStudyMembershipDialog.successfullyUpdated', {
                                    id:
                                        (currentUser as OrganisationUserGridItem | null)?.recordId ||
                                        (currentUser as OrganisationUserGridItem | null)?.id,
                                }),
                            );
                        openTrialOsStudyMembership.setFalse();
                        setCurrentUser(null);
                    }
                }}
            />
            <PageTitle text={t('uam:manageUsers')} />
            <br />
            <br />
            <Button
                id="add-users"
                style={{ display: 'flex', marginLeft: 'auto', marginBottom: 30 }}
                className="add-cv-btn main-button"
                disabled={!canEdit}
                onClick={() => {
                    openAddUser.setTrue();
                }}
            >
                {t('uam:addUser')}
            </Button>
            <UsersGrid
                items={gridItems ?? []}
                actions={(item: any) => [
                    {
                        label: 'uam:editUser',
                        icon: ['fal', 'edit'],
                        onClick: () => {
                            setCurrentUser(item);
                            openAddUser.setTrue();
                        },
                        enabled: canEdit,
                    },
                    {
                        label: 'uam:cmRamStudyMembership',
                        icon: ['fal', 'link'],
                        onClick: () => {
                            setCurrentUser(item);
                            openCmRamStudyMembership.setTrue();
                        },
                        enabled: isEnabled(item),
                    },
                    {
                        label: 'uam:smStudyMembership',
                        icon: ['fal', 'link'],
                        onClick: () => {
                            setCurrentUser(item);
                            openSMStudyMembership.setTrue();
                        },
                        enabled: isEnabled(item),
                    },
                    ...buildTrialOsStudyMembershipAction({
                        isTrialOsAdmin: !!user.isTrialOsAdmin?.(),
                        enabled: isEnabled(item),
                        onClick: () => {
                            setCurrentUser(item);
                            openTrialOsStudyMembership.setTrue();
                        },
                    }),
                    {
                        label: 'login:resetPassword',
                        icon: ['far', 'redo-alt'],
                        onClick: () => {
                            setCurrentUser(item);
                            openResetPassword.setTrue();
                        },
                        enabled: isEnabled(item) && item.isActive && canEdit && !isSso,
                    },
                ]}
                organisationName={organisation?.name}
            />
        </>
    );
};

export default AdminOrganisationMembers;
